import { trackConfig, type IFramework } from '@/framework';
import { type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, type ILayerProps, type ITextLayerProps, type Optional } from '@/version-types';
import {
    applySnapshotSize,
    applyTextRangeAttrs,
    pushHistory,
    unifyTextContentsAttrs,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IShapeText } from './shape-text';

export interface IFontStyle {
    /**
     * 设置字体 italic (font-style)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 italic
     * @param options - 配置项
     * @returns
     */
    setItalicStyle(
        layerID: string,
        flag: boolean,
        options?: { range?: Optional<TextSelectionRange> },
    ): Promise<{ layer: ILayerProps; range: TextSelectionRange | null } | void>;

    /**
     * 设置字体 bold (font-weight)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 bold
     * @param options - 配置项
     * @returns
     */
    setBoldStyle(
        layerID: string,
        flag: boolean,
        options?: { range?: Optional<TextSelectionRange> },
    ): Promise<{ layer: ILayerProps; range: TextSelectionRange | null } | void>;
}

type FontStyleKind = 'italic' | 'bold';

export class FontStyle implements IFontStyle {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    /**
     * 使用真实 Text Layer 宿主为字形样式候选重新排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param kind - 本次修改的字形样式。
     * @param flag - 是否启用该样式。
     * @param range - 可选的编辑选区。
     * @returns 可提交的图层与选区；宿主尚未就绪时返回 null。
     */
    private prepare(prevLayer: ILayerProps, kind: FontStyleKind, flag: boolean, range: TextSelectionRange | null) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: TextSelectionRange | null = range;

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(nextLayerData, range, {
                key: kind,
                value: flag,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            const { textContents: prevTextContents = [] } = prevLayerData;

            if (prevTextContents.length > 0) {
                const nextTextContents = unifyTextContentsAttrs(prevTextContents, { [kind]: flag });
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            } else {
                nextLayerData = { ...nextLayerData, [kind]: flag };
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) return { layer: nextLayer, range: nextRange };

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 非 ready 结果不属于当前候选，不能据此提交尺寸。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    private setGroup(prevLayer: ILayerProps, kind: FontStyleKind, flag: boolean) {
        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers: ILayerProps[] = [];
        for (const child of children) {
            const result = this.prepare(child, kind, flag, null);
            if (result === null) return { layer: prevLayer, range: null };

            layers.push(result.layer);
        }

        const nextLayer = { ...prevLayer, children: layers };
        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return { layer: nextLayer, range: null };
    }

    private async setSingle(
        prevLayer: ILayerProps,
        kind: FontStyleKind,
        flag: boolean,
        range: TextSelectionRange | null,
    ) {
        const result = this.prepare(prevLayer, kind, flag, range);
        if (result === null) return { layer: prevLayer, range };

        let nextLayer = result.layer;

        const { shapeExtension } = prevLayer.data as ITextLayerProps;
        if (shapeExtension) {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return { layer: nextLayer, range: result.range };
    }

    private async set(
        layerID: string,
        kind: FontStyleKind,
        flag: boolean,
        options: { range?: Optional<TextSelectionRange> } = {},
    ) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            return this.setGroup(prevLayer, kind, flag);
        }

        return this.setSingle(prevLayer, kind, flag, options.range ?? null);
    }

    setItalicStyle(layerID: string, flag: boolean, options: { range?: Optional<TextSelectionRange> } = {}) {
        return this.set(layerID, 'italic', flag, options);
    }

    setBoldStyle(layerID: string, flag: boolean, options: { range?: Optional<TextSelectionRange> } = {}) {
        return this.set(layerID, 'bold', flag, options);
    }
}
